import { supabase } from './supabase';

/**
 * Session-scoped, user-scoped, inflight-deduped cache factory.
 *
 * Replaces three repeated patterns (feature flags, CHIRON memory, morning brief)
 * with one well-tested utility. Every consumer gets:
 *  - One fetch per user per session (cache hit on subsequent calls)
 *  - Inflight dedupe by userId (N simultaneous callers → 1 network trip)
 *  - Auto-invalidation on SIGNED_OUT / SIGNED_IN / USER_UPDATED
 *  - Escape hatch via `clear()` for manual refresh after a mutation
 */
export interface UserScopedCache<T> {
  /** Fetch value (cache-first, falls through to loader on miss). */
  get: () => Promise<T>;
  /** Manually invalidate. Next get() will refetch. */
  clear: () => void;
  /** For tests only: inspect current state. */
  _inspect: () => { userId: string | null; cached: T | null; inflight: boolean };
}

/**
 * Create a user-scoped cache.
 *
 * @param loader Called with the authenticated user's id on cache miss.
 *   Return the cached value. Throw to let callers handle the error.
 * @param fallback Returned when no user is signed in.
 */
export function createUserScopedCache<T>(
  loader: (userId: string) => Promise<T>,
  fallback: T,
): UserScopedCache<T> {
  let cached: T | null = null;
  let cachedUserId: string | null = null;
  let inflight: { userId: string; promise: Promise<T> } | null = null;

  const clear = () => {
    cached = null;
    cachedUserId = null;
    inflight = null;
  };

  // Invalidate on auth events. Module-level so it fires once per cache instance.
  if (typeof window !== 'undefined') {
    supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT' || event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        clear();
      }
    });
  }

  const get = async (): Promise<T> => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return fallback;

    if (cached !== null && cachedUserId === user.id) return cached;
    if (inflight && inflight.userId === user.id) return inflight.promise;

    const promise = (async () => {
      const value = await loader(user.id);
      cached = value;
      cachedUserId = user.id;
      inflight = null;
      return value;
    })();

    inflight = { userId: user.id, promise };
    return promise;
  };

  return {
    get,
    clear,
    _inspect: () => ({ userId: cachedUserId, cached, inflight: inflight !== null }),
  };
}
